<script>
import { computed, h, ref, defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCmsContentStore, useUsersStore } from '@/stores'
import { VBtn, VIcon } from 'vuetify/components'
import { fillCmsVars } from '@/utils/cmsMessages'

function handleEditClick(e, cmsStore, key) {
  e.stopPropagation()
  e.preventDefault()
  cmsStore.openInlineEdit(key)
}

export default defineComponent({
  name: 'CmsText',
  props: {
    k: { type: String, required: true },
    tag: { type: String, default: 'span' },
    // Optional placeholder interpolation: { restaurant: 'Foo' } replaces
    // occurrences of `{restaurant}` in the rendered copy. Keys are matched
    // literally; unmatched placeholders are left intact.
    vars: { type: Object, default: () => ({}) },
  },
  setup(props, { attrs }) {
    const { t } = useI18n()
    const cmsStore = useCmsContentStore()
    const usersStore = useUsersStore()

    // The copy before its values go in: if this key has CMS content, use it directly to avoid
    // vue-i18n message compilation (CMS content may contain @, |, { } which are special in
    // vue-i18n syntax).
    const template = computed(() => {
      const cmsValue = cmsStore.contentMap[props.k]

      return cmsValue !== undefined ? cmsValue : t(props.k)
    })
    // Rich or plain is the copy's own (RM-420 comment 10): a value (a restaurant's name) never
    // makes a plain line HTML, and in a rich line every value is escaped (utils/cmsMessages).
    const isRich = computed(() => cmsStore.isRichText(props.k, template.value))
    const content = computed(() => fillCmsVars(template.value, props.vars, { html: isRich.value }))
    const isHovered = ref(false)

    const isAdmin = computed(() => usersStore.myUser?.userLevel === 'admin')
    const inEditMode = computed(() => cmsStore.editModeEnabled && isAdmin.value)

    return () => {
      // Normal render path (no edit mode or not admin)
      if (!inEditMode.value) {
        if (isRich.value) {
          return h(props.tag, { ...attrs, innerHTML: content.value })
        }

        return h(props.tag, attrs, content.value)
      }

      // Edit mode render path — same tag element with hover pencil overlay
      const hoverStyle = isHovered.value
        ? { outline: '1px dashed rgba(var(--v-theme-primary), 0.5)', outlineOffset: '2px' }
        : {}

      const children = [
        isRich.value
          ? h('span', { innerHTML: content.value })
          : content.value,
      ]

      if (isHovered.value) {
        children.push(
          h(VBtn, {
            icon: true,
            size: 'x-small',
            color: 'primary',
            style: {
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              zIndex: 150,
            },
            onClick: e => handleEditClick(e, cmsStore, props.k),
          }, () => h(VIcon, { size: '14' }, () => 'mdi-pencil')),
        )
      }

      return h(props.tag, {
        ...attrs,
        style: {
          position: 'relative',
          cursor: 'pointer',
          ...hoverStyle,
        },
        onMouseenter: () => { isHovered.value = true },
        onMouseleave: () => { isHovered.value = false },
        onClick: e => handleEditClick(e, cmsStore, props.k),
      }, children)
    }
  },
})
</script>
